import React, {
  useContext,
  useState,
  useRef,
  useEffect,
  useCallback,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { navigate } from "gatsby";
import {
  NavigationBar,
  TabContext,
  INavigationItem,
} from "@ifgengineering/component-library";
import { useTheme } from "styled-components";
import FloatingMenu, {
  FloatingMenuItemProps,
} from "@components/FloatingMenu/FloatingMenu.component";
import PoweredByIfgLogo from "@assets/images/powered-by-ifg.png";
import {
  MemberPerksPill,
  MenuContainer,
  NavChildrenContainer,
  NavigationBarContainer,
  FloatingMenuContainer,
  StyledIcon,
} from "./styled";
import { ThemedText } from "@components/ThemedText";
import Rocket from "@icon-park/react/es/icons/Rocket";
import { signOutAsync } from "@state/auth/auth.actions";
import {
  getFloatMenuItems,
  getMenuItems,
  getSelectedMenuItem,
  mainMenuNavigation,
} from "./helpers";
import {
  investorSelector,
  neverLoadedInvestorSelector,
} from "@state/investor/investor.selectors";
import Notifications from "@components/Notifications";
import { getNotificationsSelector } from "@state/notification/notification.selectors";
import { NavbarNavItemLoader } from "./NavbarItemLoader";
import { IfIsaSetupFlowModal, useIfIsaNavigation } from "@components/IfIsa";
import { isIfIsaAccountNeverLoadedSelector } from "@state/ifIsa/ifIsa.selectors";
import { useHasAccessToIfIsa } from "@hooks/useHasAccessToIfIsa";
import { useNavigationMenuClicked } from "@hooks/useNavigationMenuAnalytics";
import { isWebHomepageEnabledSelector } from "@state/remoteConfiguration/remoteConfiguration.selectors";

const MEMBER_PERKS_LABEL = "Member perks";

const NavBar: ({
  showTutorialModal,
}: {
  showTutorialModal: () => void;
}) => JSX.Element = ({ showTutorialModal }) => {
  const theme = useTheme();
  const [isMyMenuOpen, setIsMyMenuOpen] = useState(false);
  const [isHowItWorksOpen, setIsHowItWorksOpen] = useState(false);
  const [isNotificationMenuOpen, setIsNotificationMenuOpen] = useState(false);
  const [showIfIsaSetupModal, setShowIfIsaSetupModal] = useState(false);
  const dispatch = useDispatch();

  // Use the IF-ISA navigation hook
  const { handleIfIsaClick } = useIfIsaNavigation("navbar");
  const hasAccessToIfIsa = useHasAccessToIfIsa();
  const signOutUser = () => dispatch(signOutAsync());
  const investor = useSelector(investorSelector);
  // A member is already sold; the label is for everyone else.
  const isMember = investor?.isMember === true;
  const isWebHomepageEnabled = useSelector(isWebHomepageEnabledSelector);
  const neverLoadedInvestor = useSelector(neverLoadedInvestorSelector);
  const neverLoadedIfIsaAccount = useSelector(
    isIfIsaAccountNeverLoadedSelector
  );
  const notifications = useSelector(getNotificationsSelector);
  const { triggerNavigationMenuClicked } = useNavigationMenuClicked();
  const isLoading = neverLoadedInvestor || neverLoadedIfIsaAccount;

  const myMenuRef = useRef<HTMLDivElement>(null);
  const howItWorksRef = useRef<HTMLDivElement>(null);

  const userInfoToDisplay = investor?.firstName
    ? `${investor?.firstName}`
    : // Display the part before the @ instead of the name, if we don't have it
      investor?.email?.substring(0, investor?.email?.lastIndexOf("@"));

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isMyMenuOpen &&
        myMenuRef.current &&
        !myMenuRef.current.contains(event.target as Node)
      ) {
        setIsMyMenuOpen(false);
      }

      if (
        isHowItWorksOpen &&
        howItWorksRef.current &&
        !howItWorksRef.current.contains(event.target as Node)
      ) {
        setIsHowItWorksOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMyMenuOpen, isHowItWorksOpen]);

  const renderDropDownMenu = ({
    title,
    items,
    isOpen,
    onClick,
    ref,
  }: {
    title?: string;
    items: Array<Array<FloatingMenuItemProps>>;
    isOpen: boolean;
    onClick: () => void;
    ref: React.RefObject<HTMLDivElement>;
  }) => {
    const iconType = isOpen ? "Up" : "Down";
    const iconColor = isOpen
      ? theme.colors.ui.text.primary
      : theme.colors.ui.text.onColor;
    return (
      <FloatingMenuContainer
        ref={ref}
        isOpen={isOpen}
        onClick={onClick}
        data-testid="dropdown-menu-button"
      >
        <ThemedText type="S16" color={isOpen ? "SLATE900" : "SLATE200"}>
          {title}
        </ThemedText>
        <StyledIcon type={iconType} size="24px" fill={iconColor} />
        {isOpen && (
          <MenuContainer>
            <FloatingMenu collectionOfItems={items} />
          </MenuContainer>
        )}
      </FloatingMenuContainer>
    );
  };

  const handleMyMenuClick = () => {
    setIsHowItWorksOpen(false);
    setIsNotificationMenuOpen(false);
    setIsMyMenuOpen(!isMyMenuOpen);
  };

  const handleNotificationsMenuClick = (isOpen: boolean) => {
    setIsNotificationMenuOpen(isOpen);
    setIsHowItWorksOpen(false);
    setIsMyMenuOpen(false);
  };

  const { selectTab } = useContext(TabContext);

  const onClickMenuItem = useCallback(
    async (navigationItem: string, fn: () => unknown) => {
      await triggerNavigationMenuClicked({
        navigation_source: "web_menu_dropdown",
        navigation_item: navigationItem,
      });
      fn();
    },
    []
  );

  const menuItems = getFloatMenuItems({
    showTutorial: () => onClickMenuItem("Tutorial", showTutorialModal),
    logoutCallback: () => onClickMenuItem("Logout", signOutUser),
    isIfIsaOptionVisible: hasAccessToIfIsa,
    onClickMyAccount: () =>
      onClickMenuItem("Settings", () => {
        const tabId = "profile";
        navigate(`/app/account?tab=${tabId}`);
        selectTab(tabId);
      }),
    onClickDocuments: () =>
      onClickMenuItem("Documents", () => {
        navigate("/app/documents?referrer=navbar");
      }),
    onClickMembership: () =>
      onClickMenuItem("Membership", () => {
        const tabId = "membership";
        navigate(`/app/account?tab=${tabId}`);
        selectTab(tabId);
      }),
    onClickAboutUs: () =>
      onClickMenuItem("About Us", () => {
        window.open("https://cur8.capital/how-it-works", "_blank");
      }),
    onClickHelp: () =>
      onClickMenuItem("Help", () => {
        navigate("/app/help");
      }),

    onClickIfIsa: () =>
      onClickMenuItem("IF-ISA", () => {
        handleIfIsaClick(() => setShowIfIsaSetupModal(true));
      }),
  });

  const handleCompleteIfIsa = () => {
    setShowIfIsaSetupModal(false);
    navigate("/app/if-isa");
  };

  const handleSelectNavItem = useCallback(
    async (item: INavigationItem) => {
      await triggerNavigationMenuClicked({
        navigation_source: "web_navbar",
        navigation_item: item.title,
      });
      mainMenuNavigation(item.id);
    },
    [mainMenuNavigation]
  );

  return (
    <NavigationBarContainer $selectedId={getSelectedMenuItem()}>
      <NavigationBar
        navItemsTextColor="WHITE"
        selectedNavItemsTextColor="WHITE"
        bgColor="BLACK"
        selectedNavItemsBgColor="BLUE600"
        selectedItemId={getSelectedMenuItem()}
        pageName="Cur8"
        secondaryLogo={PoweredByIfgLogo}
        onLogoClick={() => navigate("/app/deals")}
        navItems={getMenuItems({
          hideBankDetails: true,
          isWebHomepageEnabled,
        })}
        onSelectNavItem={handleSelectNavItem}
      >
        <NavChildrenContainer>
          <Notifications
            notifications={notifications}
            isOpen={isNotificationMenuOpen}
            setIsOpen={handleNotificationsMenuClick}
          />
          <MemberPerksPill
            type="button"
            data-testid="member-perks"
            $iconOnly={isMember}
            aria-label={isMember ? MEMBER_PERKS_LABEL : undefined}
            onClick={() => {
              const tabId = "membership";
              navigate(`/app/account?tab=${tabId}`);
              selectTab(tabId);
            }}
          >
            <Rocket size={20} fill="currentColor" />
            {!isMember && (
              <ThemedText type="S16" color="SLATE200">
                {MEMBER_PERKS_LABEL}
              </ThemedText>
            )}
          </MemberPerksPill>
          {isLoading && <NavbarNavItemLoader />}
          {!isLoading &&
            renderDropDownMenu({
              title: userInfoToDisplay,
              items: menuItems,
              isOpen: isMyMenuOpen,
              onClick: handleMyMenuClick,
              ref: myMenuRef,
            })}
        </NavChildrenContainer>
      </NavigationBar>

      <IfIsaSetupFlowModal
        show={showIfIsaSetupModal}
        onClose={() => setShowIfIsaSetupModal(false)}
        onComplete={handleCompleteIfIsa}
        pageSource="Side Menu"
      />
    </NavigationBarContainer>
  );
};

export default NavBar;
